import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

const TOURNAMENT_TYPE_LABELS_KEY = {
  ko: 'type.ko',
  groups: 'type.groups',
  mexicano: 'type.mexicano',
  americano: 'type.americano',
  ko_placement: 'type.ko_placement',
};

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

function UserManagement() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [tournamentTypes, setTournamentTypes] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    role: 'user',
    is_active: true,
    allowed_tournament_types: []
  });

  const isMobile = useIsMobile();

  useEffect(() => {
    fetchUsers();
    fetchTournamentTypes();
  }, []);

  const fetchTournamentTypes = async () => {
    try {
      const response = await axios.get('/api/tournament-types');
      setTournamentTypes(response.data);
    } catch (err) {
      console.error('Error loading tournament types:', err);
    }
  };

  const toggleAllowedType = (type) => {
    setFormData(prev => {
      const current = prev.allowed_tournament_types || [];
      const updated = current.includes(type)
        ? current.filter(tp => tp !== type)
        : [...current, type];
      return { ...prev, allowed_tournament_types: updated };
    });
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError(t('userMgmt.loadError') + ': ' + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        email: formData.email.trim() === '' ? null : formData.email,
        first_name: formData.first_name.trim() === '' ? null : formData.first_name,
        last_name: formData.last_name.trim() === '' ? null : formData.last_name,
        allowed_tournament_types: formData.role === 'tournament_admin' ? formData.allowed_tournament_types : []
      };
      await axios.post('/api/users', submitData);
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
      alert(t('userMgmt.createSuccess'));
    } catch (err) {
      console.error('Error creating user:', err.response?.data);
      let errorMsg = t('create.error') + ':\n\n';
      if (err.response?.data?.detail) {
        if (Array.isArray(err.response.data.detail)) {
          errorMsg += err.response.data.detail.map(e => `- ${e.loc?.join(' > ')}: ${e.msg}`).join('\n');
        } else {
          errorMsg += err.response.data.detail;
        }
      } else {
        errorMsg += err.message;
      }
      alert(errorMsg);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        ...formData,
        email: formData.email.trim() === '' ? null : formData.email,
        first_name: formData.first_name.trim() === '' ? null : formData.first_name,
        last_name: formData.last_name.trim() === '' ? null : formData.last_name,
        allowed_tournament_types: formData.role === 'tournament_admin' ? formData.allowed_tournament_types : []
      };
      if (!updateData.password) delete updateData.password;
      await axios.put(`/api/users/${editingUser.id}`, updateData);
      setEditingUser(null);
      resetForm();
      fetchUsers();
      alert(t('userMgmt.updateSuccess'));
    } catch (err) {
      alert(t('common.error') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(t('userMgmt.confirmDelete', { username }))) return;
    try {
      await axios.delete(`/api/users/${userId}`);
      fetchUsers();
      alert(t('userMgmt.deleteSuccess'));
    } catch (err) {
      alert(t('common.error') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleActive = async (userId, username, currentStatus) => {
    const action = currentStatus ? t('userMgmt.deactivate').toLowerCase() : t('userMgmt.activate').toLowerCase();
    if (!window.confirm(t('userMgmt.confirmToggle', { username, action }))) return;
    try {
      await axios.patch(`/api/users/${userId}/toggle-active`, {});
      fetchUsers();
    } catch (err) {
      alert(t('common.error') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      first_name: '',
      last_name: '',
      email: '',
      role: 'user',
      is_active: true,
      allowed_tournament_types: []
    });
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      role: user.role,
      is_active: user.is_active,
      allowed_tournament_types: user.allowed_tournament_types || []
    });
    setShowCreateModal(true);
  };

  const getRoleName = (role) => t(`role.${role}`) || role;

  const getRoleBadgeClasses = (role) => {
    if (role === 'full_admin') return 'bg-red-500/20 text-red-300';
    if (role === 'tournament_admin') return 'bg-blue-500/20 text-blue-300';
    return 'bg-slate-500/20 text-slate-400';
  };

  if (loading) {
    return <div className="text-center p-10 text-slate-500">{t('userMgmt.loading')}</div>;
  }

  if (error) {
    return <div className="text-center p-10 text-red-400">{error}</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white m-0">
          {t('userMgmt.title')}
        </h1>
        <button
          onClick={() => { resetForm(); setEditingUser(null); setShowCreateModal(true); }}
          className="bg-gradient-to-br from-gold to-gold-dark text-slate-900 px-5 py-2.5 text-sm sm:text-base border-none rounded-lg font-semibold cursor-pointer whitespace-nowrap hover:shadow-lg transition-shadow"
        >
          {t('userMgmt.newUserBtn')}
        </button>
      </div>

      {/* Desktop Table View */}
      {!isMobile && (
        <div className="w-full">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-200 dark:border-slate-700">
                <th className="p-3 lg:p-4 text-left font-semibold text-slate-900 dark:text-white">{t('userMgmt.username')}</th>
                <th className="p-3 lg:p-4 text-left font-semibold text-slate-900 dark:text-white">{t('userMgmt.name')}</th>
                <th className="p-3 lg:p-4 text-left font-semibold text-slate-900 dark:text-white hidden lg:table-cell">Email</th>
                <th className="p-3 lg:p-4 text-left font-semibold text-slate-900 dark:text-white">{t('userMgmt.role')}</th>
                <th className="p-3 lg:p-4 text-center font-semibold text-slate-900 dark:text-white">{t('userMgmt.status')}</th>
                <th className="p-3 lg:p-4 text-right font-semibold text-slate-900 dark:text-white">{t('userMgmt.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr
                  key={user.id}
                  className={`transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${idx < users.length - 1 ? 'border-b border-slate-200 dark:border-slate-700' : ''}`}
                >
                  <td className="p-3 lg:p-4">
                    <span className="font-medium text-slate-900 dark:text-white">{user.username}</span>
                  </td>
                  <td className="p-3 lg:p-4 text-slate-700 dark:text-slate-300">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : '-'}
                  </td>
                  <td className="p-3 lg:p-4 text-slate-600 dark:text-slate-400 hidden lg:table-cell">{user.email || '-'}</td>
                  <td className="p-3 lg:p-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getRoleBadgeClasses(user.role)}`}>
                      {getRoleName(user.role)}
                    </span>
                    {user.role === 'tournament_admin' && user.allowed_tournament_types?.length > 0 && (
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {user.allowed_tournament_types.map(tp => t(TOURNAMENT_TYPE_LABELS_KEY[tp] || tp)).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="p-3 lg:p-4 text-center">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400'}`}>
                      {user.is_active ? t('userMgmt.active') : t('userMgmt.inactive')}
                    </span>
                  </td>
                  <td className="p-3 lg:p-4">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => startEdit(user)}
                        className="bg-gradient-to-br from-gold to-gold-dark text-slate-900 px-3 py-1.5 text-xs font-medium border-none rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleToggleActive(user.id, user.username, user.is_active)}
                        className={`px-3 py-1.5 text-xs font-medium border-none rounded-md cursor-pointer text-white hover:opacity-90 transition-opacity ${user.is_active ? 'bg-amber-500' : 'bg-green-500'}`}
                      >
                        {user.is_active ? t('userMgmt.deactivate') : t('userMgmt.activate')}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        className="bg-red-500 text-white px-3 py-1.5 text-xs font-medium border-none rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Card View */}
      {isMobile && (
        <div className="flex flex-col gap-3">
          {users.map((user) => (
            <div
              key={user.id}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="font-semibold text-lg text-slate-900 dark:text-white mb-1">
                    {user.username}
                  </div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : '-'}
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-xl text-xs font-medium whitespace-nowrap ${user.is_active ? 'bg-green-500/20 text-green-300' : 'bg-slate-500/20 text-slate-400'}`}>
                  {user.is_active ? t('userMgmt.active') : t('userMgmt.inactive')}
                </span>
              </div>

              <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                {user.email || t('userMgmt.noEmail')}
              </div>

              <div className="mb-3">
                <span className={`px-3 py-1 rounded-xl text-sm font-medium ${getRoleBadgeClasses(user.role)}`}>
                  {getRoleName(user.role)}
                </span>
                {user.role === 'tournament_admin' && user.allowed_tournament_types?.length > 0 && (
                  <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    {user.allowed_tournament_types.map(tp => t(TOURNAMENT_TYPE_LABELS_KEY[tp] || tp)).join(', ')}
                  </div>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => startEdit(user)}
                  className="flex-1 bg-gradient-to-br from-gold to-gold-dark text-slate-900 px-4 py-2 text-sm border-none rounded-md font-semibold cursor-pointer"
                >
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleToggleActive(user.id, user.username, user.is_active)}
                  className={`flex-1 px-4 py-2 text-sm border-none rounded-md font-semibold cursor-pointer text-white ${user.is_active ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-green-500 to-green-600'}`}
                >
                  {user.is_active ? t('userMgmt.deactivate') : t('userMgmt.activate')}
                </button>
                <button
                  onClick={() => handleDeleteUser(user.id, user.username)}
                  className="flex-1 bg-gradient-to-br from-red-500 to-red-600 text-white px-4 py-2 text-sm border-none rounded-md font-semibold cursor-pointer"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-modal">
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-xl w-[90%] max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white">
              {editingUser ? t('userMgmt.editUser') : t('userMgmt.newUser')}
            </h2>

            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="grid gap-5">
                {/* Username */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.username')} *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    required
                    disabled={!!editingUser}
                    className={`w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 text-base text-slate-900 dark:text-white ${editingUser ? 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed' : 'bg-white dark:bg-slate-700'}`}
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.password')} {editingUser ? `(${t('userMgmt.passwordHint')})` : '*'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required={!editingUser}
                    placeholder={!editingUser ? t('userMgmt.passwordReq') : ''}
                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-base text-slate-900 dark:text-white"
                  />
                  {!editingUser && (
                    <small className="block mt-1 text-slate-500 text-sm">
                      {t('userMgmt.passwordReqShort')}
                    </small>
                  )}
                </div>

                {/* First Name */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.firstName')}
                  </label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-base text-slate-900 dark:text-white"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.lastName')}
                  </label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-base text-slate-900 dark:text-white"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.emailOptional')}
                  </label>
                  <input
                    type="text"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-base text-slate-900 dark:text-white"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block mb-2 font-medium text-slate-900 dark:text-white">
                    {t('userMgmt.role')} *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                    required
                    className="w-full p-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-base text-slate-900 dark:text-white"
                  >
                    <option value="user">{t('role.user')}</option>
                    <option value="tournament_admin">{t('role.tournament_admin')}</option>
                    <option value="full_admin">{t('role.full_admin')}</option>
                  </select>
                </div>

                {/* Allowed tournament types – only for tournament admins */}
                {formData.role === 'tournament_admin' && tournamentTypes.length > 0 && (
                  <div>
                    <label className="block mb-1 font-medium text-slate-900 dark:text-white">
                      {t('userMgmt.allowedTypes')}
                    </label>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
                      {t('userMgmt.allowedTypesHint')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {tournamentTypes.map(type => (
                        <label
                          key={type}
                          className="flex items-center gap-2.5 p-2.5 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={(formData.allowed_tournament_types || []).includes(type)}
                            onChange={() => toggleAllowedType(type)}
                            className="w-4 h-4 accent-gold flex-shrink-0"
                          />
                          <span className="text-sm text-slate-900 dark:text-white">
                            {t(TOURNAMENT_TYPE_LABELS_KEY[type] || type)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Active Status */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    id="is_active"
                    className="w-5 h-5 accent-gold"
                  />
                  <label htmlFor="is_active" className="font-medium cursor-pointer text-slate-900 dark:text-white">
                    {t('userMgmt.isActive')}
                  </label>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 mt-8">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-br from-green-500 to-green-600 text-white p-3 text-base font-semibold border-none rounded-lg cursor-pointer hover:opacity-90"
                >
                  {editingUser ? t('common.save') : t('common.create')}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setEditingUser(null); resetForm(); }}
                  className="flex-1 bg-gradient-to-br from-slate-500 to-slate-600 text-white p-3 text-base font-semibold border-none rounded-lg cursor-pointer hover:opacity-90"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
